import { useState } from 'react';
import { Typography, Card, Row, Col, Radio, List, Button, Modal, Form, Input, InputNumber, Select, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBoards, getSubjects, getChapters, createSubject, createChapter } from '@/services/curriculum';
import { BOARD_OPTIONS } from '@/constants';
import { Board, Subject, Chapter } from '@/types';

export default function CurriculumPage() {
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [subjectModal, setSubjectModal] = useState(false);
  const [chapterModal, setChapterModal] = useState(false);
  const [subjectForm] = Form.useForm();
  const [chapterForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: boardsData } = useQuery({ queryKey: ['boards'], queryFn: getBoards });
  const boards: Board[] = boardsData?.data ?? [];

  // Create a combined list of API boards and fallback options
  const allBoardOptions = boards.length > 0 
    ? boards.map(board => board.name)
    : BOARD_OPTIONS;

  const boardId = boards.find((b) => b.code === selectedBoard || b.name === selectedBoard)?.id;

  const { data: subjectsData } = useQuery({
    queryKey: ['subjects', boardId],
    queryFn: () => getSubjects(boardId),
    enabled: !!boardId,
  });
  const subjects: Subject[] = (subjectsData as { data?: Subject[] })?.data ?? (Array.isArray(subjectsData) ? subjectsData as Subject[] : []);

  const { data: chaptersData } = useQuery({
    queryKey: ['chapters', selectedSubject],
    queryFn: () => getChapters(selectedSubject!),
    enabled: !!selectedSubject,
  });
  const chapters: Chapter[] = (chaptersData as { data?: Chapter[] })?.data ?? (Array.isArray(chaptersData) ? chaptersData as Chapter[] : []);

  const subjectMutation = useMutation({
    mutationFn: createSubject,
    onSuccess: () => { message.success('Subject created'); queryClient.invalidateQueries({ queryKey: ['subjects'] }); setSubjectModal(false); subjectForm.resetFields(); },
    onError: () => message.error('Failed to create subject'),
  });

  const chapterMutation = useMutation({
    mutationFn: createChapter,
    onSuccess: () => { message.success('Chapter created'); queryClient.invalidateQueries({ queryKey: ['chapters'] }); setChapterModal(false); chapterForm.resetFields(); },
    onError: () => message.error('Failed to create chapter'),
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Curriculum</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Manage boards, subjects, and chapters</Typography.Text>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8} lg={6}>
          <Card title="Boards">
            <Radio.Group value={selectedBoard} onChange={(e) => { setSelectedBoard(e.target.value); setSelectedSubject(null); }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allBoardOptions.map((boardName) => {
                const board = boards.find(b => b.name === boardName);
                return (
                  <Radio key={boardName} value={boardName}>
                    {boardName}
                    {board && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b' }}>
                        ({board.code})
                      </span>
                    )}
                  </Radio>
                );
              })}
            </Radio.Group>
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={9}>
          <Card title="Subjects" extra={boardId && <Button icon={<PlusOutlined />} size="small" onClick={() => setSubjectModal(true)}>Add</Button>}>
            {!selectedBoard ? <Typography.Text type="secondary">Select a board</Typography.Text> : (
              <List
                dataSource={subjects}
                renderItem={(item) => (
                  <List.Item onClick={() => setSelectedSubject(item.id)} style={{ cursor: 'pointer', background: selectedSubject === item.id ? '#f0f5ff' : undefined, padding: '8px 12px' }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 6, backgroundColor: item.colorHex || '#ccc', marginRight: 8 }} />
                    {item.name}
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={9}>
          <Card title="Chapters" extra={selectedSubject && <Button icon={<PlusOutlined />} size="small" onClick={() => setChapterModal(true)}>Add</Button>}>
            {!selectedSubject ? <Typography.Text type="secondary">Select a subject</Typography.Text> : (
              <List
                dataSource={chapters}
                renderItem={(item) => (
                  <List.Item><Typography.Text>{item.sequenceOrder}. {item.name}</Typography.Text> <Typography.Text type="secondary">Grade {item.gradeId}</Typography.Text></List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="Add Subject" open={subjectModal} onCancel={() => setSubjectModal(false)} onOk={() => subjectForm.submit()} confirmLoading={subjectMutation.isPending}>
        <Form form={subjectForm} layout="vertical" onFinish={(values) => boardId && subjectMutation.mutate({ ...values, boardId })}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="gradeIds" label="Grades" rules={[{ required: true, message: 'Select at least one grade' }]}>
            <Select mode="multiple" placeholder="Select grades">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                <Select.Option key={g} value={g}>Grade {g}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="colorHex"
            label="Subject Color"
            getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => e.target.value}
          >
            <input
              type="color"
              style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: '1px solid #d9d9d9', cursor: 'pointer', verticalAlign: 'middle' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Chapter" open={chapterModal} onCancel={() => setChapterModal(false)} onOk={() => chapterForm.submit()} confirmLoading={chapterMutation.isPending}>
        <Form form={chapterForm} layout="vertical" onFinish={(values) => selectedSubject && chapterMutation.mutate({ ...values, subjectId: selectedSubject })}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="gradeId" label="Grade (1-12)" rules={[{ required: true }]}><InputNumber min={1} max={12} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="sequenceOrder" label="Sequence Order" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
